import { createSlice, type PayloadAction } from "@reduxjs/toolkit";
import {
  REQUEST_STATUS,
  type RequestStatus,
} from "@/modules/constants/REQUEST_STATUS";
import { type SerializedError } from "@/functions/serializeError";
import type { Ticket } from "../ticketSharedTypes";
import type { RootState } from "@/store/store";
import { TicketProcessingStateEnum } from "@/api/hausify";
import { fetchTicketsByEstateIdThunk } from "./ticketListOperations";

interface TicketListState {
  items: Ticket[];
  status: RequestStatus;
  error: SerializedError | null;
  filterItems: string[];
}

const initialState: TicketListState = {
  items: [],
  status: REQUEST_STATUS.IDLE,
  error: null,
  filterItems: [
    TicketProcessingStateEnum.InProgress,
    TicketProcessingStateEnum.New,
    TicketProcessingStateEnum.WorkDone,
  ],
};

const ticketsSlice = createSlice({
  name: "ticketList",
  initialState,
  reducers: {
    createTicket: (state, action: PayloadAction<Ticket>) => {
      state.items = [action.payload, ...state.items];
    },
    updateTicket: (state, action: PayloadAction<Ticket>) => {
      const idx = state.items.findIndex((t) => t.id === action.payload.id);
      if (idx !== -1) state.items[idx] = action.payload;
    },
    deleteTicket: (state, action: PayloadAction<string>) => {
      state.items = state.items.filter((t) => t.id !== action.payload);
    },
    setTicketListFilter(state, action: PayloadAction<string>) {
      if (action.payload === "ALL") {
        state.filterItems = [
          TicketProcessingStateEnum.InProgress,
          TicketProcessingStateEnum.New,
          TicketProcessingStateEnum.WorkDone,
        ];
        return;
      }
      state.filterItems = [action.payload];
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchTicketsByEstateIdThunk.pending, (state) => {
        state.status = REQUEST_STATUS.LOADING;
        state.error = null;
      })
      .addCase(fetchTicketsByEstateIdThunk.fulfilled, (state, action) => {
        state.status = REQUEST_STATUS.SUCCEEDED;
        state.items = action.payload || [];
      })
      .addCase(fetchTicketsByEstateIdThunk.rejected, (state, action) => {
        state.status = REQUEST_STATUS.FAILED;
        state.error = (action.payload as SerializedError) || {
          message: action.error.message || "Unknown error",
        };
      });
  },
});

export const ticketListActions = ticketsSlice.actions;
export const ticketsReducer = ticketsSlice.reducer;

// Selectors
export const selectTicketItems = (state: RootState): Ticket[] => {
  const list = state?.tickets?.ticketsList;
  if (!list) return [];
  return Array.isArray(list) ? list : list.items || [];
};

export const selectTicketListFilter = (state: RootState): string[] => {
  return state?.tickets?.ticketsList?.filterItems || [];
};

const selectTicketsSlice = (state: RootState): TicketListState | null => {
  const slice = state?.tickets?.ticketsList;
  return slice as TicketListState | null;
};

export const selectTicketsStatus = (state: RootState): RequestStatus => {
  const slice = selectTicketsSlice(state);
  if (!slice) return "idle";
  return slice.status || "idle";
};

const selectTicketsError = (state: RootState): SerializedError | null => {
  const slice = selectTicketsSlice(state);
  return slice?.error || null;
};
